import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { pick } from 'ramda'
import useReactRouter from 'use-react-router'

import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { routes } from 'core/utils/routes'
import DocumentMeta from 'core/components/DocumentMeta'
import DataKeys from 'k8s/DataKeys'
import ClusterNameCell from 'k8s/components/common/cells/ClusterNameCell'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { ArrayElement } from 'core/actions/Action'
import ListContainer from 'core/containers/ListContainer'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import InferActionParams from 'core/actions/InferActionParams'
import { listCustomResourceDefinitions } from './actions'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import StringMultiDropdownFilter from 'core/elements/grid/filters/StringMultiDropdownFilter'
import { DateAndTime } from 'core/components/listTable/cells/DateCell'

import {
  ICustomResourceDefinitionDetailsPageTabs,
  ICustomResourceDefinitionSelector,
} from './model'
import { customResourceDefinitionsSelector, createGroupOptionsSelector } from './selectors'
import AddCustomResourceDefinitionModal from './AddCustomResourceDefinitionModal'
import DeleteCustomResourceDefinitionDialog from './DeleteCustomResourceDefinitionDialog'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import { GridDropdownBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'

type ModelDataKey = DataKeys.CustomResourceDefinitions
type SelectorModel = ArrayElement<ReturnType<typeof customResourceDefinitionsSelector>>
type ActionParams = InferActionParams<typeof listCustomResourceDefinitions>
type Params = ActionParams & {
  masterNodeClusters: boolean
  healthyClusters: boolean
}

const requiredParams: Array<keyof ActionParams> = ['clusterId']
const defaultParams: Params & SortingState = {
  clusterId: null,
  masterNodeClusters: true,
  healthyClusters: true,
  orderBy: 'name',
  orderDirection: 'asc',
}

const usePrefParams = createUsePrefParamsHook<ActionParams & TablePrefsParams>(
  'CustomResourceDefinitions',
  listTablePrefs,
)
const searchTargets = ['name', 'clusterName']

export const getCustomResourceDefinitionsColumns = (): GridViewColumn<SelectorModel>[] => {
  return [
    {
      key: 'name',
      label: 'Name',
      width: 'medium',
      CellComponent: createGridLinkCell({
        routeToFn: ({ clusterId, id }) =>
          routes.clusterAdministration.customResourceDefinitions.details.path({
            clusterId,
            id,
            tab: ICustomResourceDefinitionDetailsPageTabs.Overview,
          }),
      }),
    },
    {
      key: 'clusterName',
      label: 'Cluster',
      CellComponent: ClusterNameCell,
    },
    {
      key: 'group',
      label: 'Group',
    },
    {
      key: 'scope',
      label: 'Scope',
    },
    {
      key: 'latestVersion',
      label: 'Latest version',
    },
    {
      key: 'creationTimestamp',
      label: 'Created',
      CellComponent: DateAndTime,
    },
    {
      key: 'labels',
      label: 'Labels',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
    },
  ]
}

export default function CustomResourceDefinitionsListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { message, loading, reload } = useListAction(listCustomResourceDefinitions, {
    params,
    requiredParams,
  })

  const customResourceDefinitions: ICustomResourceDefinitionSelector[] = useSelector(
    customResourceDefinitionsSelector,
  )
  const groupOptions = useSelector(createGroupOptionsSelector)
  const columns = getCustomResourceDefinitionsColumns()
  const [selectedCustomResourceDefinition, setSelectedCustomResourceDefinition] = useState<
    SelectorModel
  >(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)
  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (customResourceDefinition) => {
        setSelectedCustomResourceDefinition(customResourceDefinition)
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
    },
  ]

  const dropdownFilters = useMemo(
    () => [
      {
        key: 'group',
        label: 'By Group',
        FilterComponent: StringMultiDropdownFilter,
        filterComponentProps: {
          label: 'By Group',
        },
        filterComponentOptionsPropName: 'dropdownOptions',
        getOptionsFn: () => {
          return groupOptions
        },
        equalityComparerFn: (item, value) => {
          return value.includes(item?.spec?.group)
        },
      },
    ],
    [groupOptions],
  )

  const dropdownBatchActions: GridDropdownBatchActionSpec<SelectorModel>[] = useMemo(
    () => [
      {
        label: 'Delete',
        BatchActionButton: getGridDialogButton(DeleteCustomResourceDefinitionDialog, {
          action: 'delete',
        }),
      },
    ],
    [],
  )

  return (
    <>
      <DocumentMeta title="Custom Resource Definitions" />
      <AddCustomResourceDefinitionModal
        addRoute={routes.clusterAdministration.customResourceDefinitions.add}
      />
      {showDeleteDialog && (
        <DeleteCustomResourceDefinitionDialog
          onClose={() => setShowDeleteDialog(false)}
          rows={[selectedCustomResourceDefinition]}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.CustomResourceDefinitions}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={customResourceDefinitions}
        columns={columns}
        addUrl={routes.clusterAdministration.customResourceDefinitions.add.path()}
        addText="Add New"
        rowMenuItems={rowMenuItems}
        dropdownFilters={dropdownFilters}
        getParamsUpdater={getParamsUpdater}
        showItemsCountInLabel
        showRowMenuForSingleRowActions
        disableColumnOrdering
        batchActions={dropdownBatchActions}
        multiSelection
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
